/* global React, G */
import { Children, cloneElement, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Grid, useTheme } from '@mui/material'
import { PlatformEvent } from 'lib/util'
import { preload, useStyles } from '@platform/react/hook'
import ApplicationContext from 'platform/react/context/application'
import Icon from 'ui/Element/Icon'
import ErrorBoundary from 'ui/Error'
import ScrollGrid from 'ui/Element/Grid/Scrollable'
import OverflowTooltip from 'ui/Element/Text/OverflowTooltip'
import Button from 'ui/Element/Button'
import MuiListTemplateButton from 'ui/Element/Attachment/List/Template/Button'

const styles = (theme, { largeTitle }) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  progress: {
    maxWidth: '100%',
    height: '3rem',
    marginBottom: '-3.5rem',
    borderRadius: '0.5rem',
    backgroundColor: 'transparent',
    '& > *': {
      backgroundColor: theme.palette.background.light,
    },
  },
  folderTitle: {
    maxWidth: 'calc(100% - 3rem)',
    color: theme.palette.black.main,
  },
  scrollContainer: {
    height: 'calc(100% - 6.5rem)',
  },
  scrollContent: {
    flexDirection: 'column',
    gap: '1rem',
  },
  folderList: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: '0.5rem',
    backgroundColor: theme.palette.white.main,
    borderRadius: '0.5rem',
  },
  skipTitle: {
    display: 'flex',
    flexDirection: 'row',
    gap: '1rem',
  },
  divider: {
    borderColor: `${theme.palette.gray['900']}!important`,
  },
  folderRow: {
    cursor: 'pointer',
    padding: '0.75rem',
    display: 'flex',
    maxWidth: '100%',
    minHeight: '3rem',
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: theme.palette.black.main,
    '&:first-child': {
      marginTop: '0!important',
    },
    '&:last-child': {
      marginBottom: '0!important',
    },
    '& > *': {
      zIndex: 10, // text should be above progress bar
    },
    '&:hover': {
      borderRadius: '0.5rem',
      backgroundColor: theme.palette.background.light,
    },
  },
  icon: {
    cursor: 'pointer',
    '& .MuiButton-root': {
      padding: 0,
      width: '1.5rem',
      minWidth: '1.5rem', // defaults to 64 with MuiButton
      height: '1.5rem',
      minHeight: '1.5rem', // defaults to 40 with MuiButton
      '&:hover': {
        backgroundColor: 'inherit',
      },
    },
  },
  breadcrumbs: {
    marginBottom: '1rem',
  },
  header: {
    display: 'flex',
    flexWrap: 'nowrap',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: '1.5625rem',
    alignItems: 'center',
    // without this, the breadcrumbs will shift down
    // if we display an icon due to alignItems: 'center'
    minHeight: '2.25rem',
  },
  title: {
    width: '100%',
    display: 'flex',
    maxWidth: `calc(100% - ${largeTitle ? 1 : 10}rem)`,
    flexDirection: 'row',
    gap: '0.75rem',
    alignItems: 'center',
    '& > div:first-child': {
      '& > *': { // rotating arrow icon
        transform: 'rotate(180deg)',
        cursor: 'pointer',
      },
    },
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: '0.75rem',
  },
  document: {
    height: '31.25rem',
    display: 'flex',
    color: theme.palette.black.main,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  documentIcon: {
    fontSize: '5rem',
    marginBottom: '1.875rem',
  },
  documentLabel: {
    fontWeight: 700,
    marginBottom: '0.75rem',
  },
  documentText: {
    marginBottom: '1.6875rem',
  },
  downloadButton: {
    '& .MuiButton-root': {
      borderRadius: '0.5rem',
      boxShadow: 'none',
    },
  },
  downloadSize: {
    color: theme.palette.text.description,
  },
  ...theme.custom.fileFolder,
})

/**
 * Title Component
 *
 * Renders the title of the current node, together with a back button and an optional
 * download button
 *
 * @param {Object} node               the node to render
 * @param {string} title              the title of the node
 * @param {string} type               the type of the node
 * @param {Object} attachment         optional attachment of the node
 * @param {Object} classes            classes for stylling
 * @param {Object} events             events for the component
 * @param {Function} events.onClick   handler for clicking on the back button
 * @param {Object} options            additional props
 * @returns {JSX.Element}
 */
const Title = ({ node, title, type, attachment, classes, events, ...options }) => {
  const { eventBus } = useContext(ApplicationContext)
  const { onClick, onFavoriteClick = null, onFavoriteOpen = null, onLinkClick } = events
  const { showFavorite, icons, labels, showDeepLink, downloadAll } = options
  const { linkIcon } = icons

  // Disabled for now, see NOTE in SP-1505
  // const showFavoriteIcon = showFavorite && type === 'File'

  // Disabled for now, see NOTE in SP-1505
  // const isFavorite = !showFavoriteIcon
  //   ? false
  //   : onFavoriteOpen?.(new PlatformEvent('open', { value: node?.documentationId || node?.attachmentId }))

  // Disabled for now, see NOTE in SP-1505
  // const favoriteEvent = event => new PlatformEvent(event, {
  //   payload: node?.documentationId || node?.attachmentId,
  //   options: {
  //     type: 'documentation',
  //     delete: isFavorite,
  //   },
  // })

  const handleLinkClick = useCallback((e) => {
    const clickEvent = new PlatformEvent(e, { attachment })
    const uri = onLinkClick?.(clickEvent)
    const externalUrl = `${window.location.origin}/${uri}`

    navigator.clipboard
      .writeText(externalUrl)
      .then(() => {
        eventBus.dispatch(eventBus.type(G.DATA, G.UPDATE), {
          message: labels.linkCopied,
          severity: 'success',
          close: 5000,
        })
      })
  }, [attachment])

  // Disabled for now, see NOTE in SP-1505
  // const favoriteIcon = icons.favorite
  //   ? <Icon
  //     size={24}
  //     shape={'circle'}
  //     iconPadding={8}
  //     background={'white'}
  //     color={'error.700'}
  //     source={icons.favorite || 'favorite-filled'}
  //   />
  //   : <FavoriteIcon/>

  // Disabled for now, see NOTE in SP-1505
  // const noFavoriteIcon = icons.noFavorite
  //   ? <Icon
  //     size={24}
  //     shape={'circle'}
  //     iconPadding={8}
  //     background={'white'}
  //     color={'black'}
  //     source={icons.noFavorite || 'favorite}
  //   />
  //   : <FavoriteIcon/>

  const canDownloadAll = node?.depth === 0 && attachment?.value?.name && downloadAll

  return (
    <Grid
      container
      className={classes.header}
    >
      <Grid
        item
        className={classes.title}
      >
        {node?.parent?.id && (
          <Box
            onClick={() => onClick(node.parent.id)}
          >
            <Icon
              name={'arrow-right'}
              color={'black'}
              background={'white'}
              iconPadding={1}
              size={'1.5rem'}
              shape={'circle'}
            />
          </Box>
        )}
        <OverflowTooltip variant={'24/bold'}>
          {title}
        </OverflowTooltip>
        {/* Disabled for now, see NOTE in SP-1505 */}
        {/* {showFavoriteIcon && ( */}
        {/*  <Box */}
        {/*    sx={{ cursor: 'pointer' }} */}
        {/*    onClick={e => onFavoriteClick?.(favoriteEvent(e))} */}
        {/*  > */}
        {/*    {isFavorite ? favoriteIcon : noFavoriteIcon} */}
        {/*  </Box> */}
        {/* )} */}
      </Grid>
      <Grid
        item xs={'auto'}
        className={classes.actions}
      >
        {showDeepLink && type === 'File' && (
          <Button
            startIcon={{
              ...linkIcon,
              color: 'white',
            }}
            events={{ onClick: handleLinkClick }}
            size={'medium'}
            variant={'contained'}
            fullWidth={false}
            value={labels.copyLink}
          />
        )}
        {canDownloadAll && (
          <MuiListTemplateButton
            fullWidth={true}
            color={'primary'}
            variant={'contained'}
            attachment={attachment}
            value={labels.downloadAll}
            endIcon={icons.download}
          />
        )}
      </Grid>
    </Grid>
  )
}

/**
 * Directory Component
 *
 * Responsible for displaying a directory and its content.
 *
 * @param {Object} node               the directory to render
 * @param {Object} classes            styling for the component
 * @param {Object} events             events for the directory
 * @param {Object} labels             labels for the directory
 * @param {Object} icons              icons for the directory
 * @param {React.ReactNode} ListItem  template to use for a single directory child
 * @param {Object} options            additional props
 * @returns {JSX.Element}
 */
const Directory = ({ node, classes, events, labels, icons, ListItem, ...options }) => {
  const { onClick, onSkip, onScroll, onNodeType, onNodeLabel } = events

  const handleScroll = useCallback((event) => {
    onScroll?.(new PlatformEvent(event, { ...event.detail, id: node.id }))
  }, [node, onScroll])

  const skipIcon = icon => (
    <Icon
      source={icon}
      name={'skip-forward'}
      color={'gray.500'}
    />
  )

  return (
    <ScrollGrid
      container
      component={'main'}
      justifyContent={'flex-start'}
      scrollProps={{
        onScroll: handleScroll,
        position: { y: options.scrollPosition || 0 },
      }}
      classes={{ root: classes.scrollContainer, content: classes.scrollContent }}
    >
      {node?.skippable && (
        <Grid
          container
          onClick={onSkip}
          sx={{ cursor: 'pointer' }}
          className={classes.folderList}
        >
          <Grid
            container
            className={classes.folderRow}
          >
            <Grid
              item
              className={classes.skipTitle}
            >
              {!options.hideSkipIcon && (
                node?.icon
                  ? skipIcon(node.icon)
                  : skipIcon(options.skipIcon)
              )}
              <OverflowTooltip variant={'14/medium'}>
                {labels.skipLabel}
              </OverflowTooltip>
            </Grid>
            <Grid
              item
              className={classes.icon}
            >
              <Icon
                source={icons.forwardIcon}
                color={'gray.500'}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
      <Box className={classes.folderList}>
        {node?.$children?.length && node.$children.map((item, index) => (
          <ListItem
            key={index}
            node={item}
            group={options.group}
            types={options.types}
            fileIcons={options.fileIcons}
            header={item?.header || null}
            footer={item?.footer || null}
            lastItem={index === node.$children.length - 1}
            events={{ onClick, onNodeType, onNodeLabel }}
            attachment={item.attachment ? {
              ...item.attachment,
              key: item.attachmentId,
              uuid: item.id,
            } : null}
            {...{
              downloadIcon: icons.downloadIcon,
              forwardIcon: icons.forwardIcon,
              skipSingleNode: icons.skipSingleNode,
              notifyOnSkippable: icons.notifyOnSkippable,
              classes,
            }}
          />
        ))
        }
      </Box>
    </ScrollGrid>
  )
}

/**
 * Helper function to map an attachment type to a certain structure.
 *
 * @param {Object} node the node to map
 * @returns {{url: {value: {name: *, url, documentationId: *}}, attachment: (*&{key, documentationId: *})}}
 * @private
 */
const _nodeAttachmentMapping = node => ({
  url: {
    value: {
      name: node?.realName || node.url?.split('/')?.at(-1) || node?.name,
      url: node.url,
      documentationId: node?.documentationId,
    },
  },
  attachment: {
    ...node.attachment,
    key: node.attachmentId,
    documentationId: node?.documentationId,
  },
})

/**
 * Render the contents of the current node.
 *
 * @param {Object} node                   the current node to render.
 * @param {Object} events                 events passed from parent component
 * @param {Function} events.onAttachment  handler responsible for doing something with a single
 *                                        attachment
 * @param {Object} classes                styles to apply
 * @param {React.ReactNode} ListItem      component to use for rendering sub folders
 * @param {Object} children               children to render
 * @param {Object} options                options passed from parent component
 * @returns {JSX.Element}
 * @private
 */
const _content = (node, events, { classes, listItem: ListItem, children, ...options }) => {
  const attachment = (node?.url && _nodeAttachmentMapping(node).url)
      || (node?.attachment && _nodeAttachmentMapping(node).attachment)
      || { value: { name: '' } }

  const { onNodeType, onAttachment, onBreadcrumbs, onNodeLabel = null } = events
  const scrollPositionsRef = useRef({})

  const handleClick = useCallback((eventOrId) => {
    const { detail = {} } = eventOrId
    const id = detail.id || eventOrId
    const clickEvent = new PlatformEvent('click', { ...detail, jump: true, back: true, id })
    events?.onClick?.(clickEvent)
  }, [events])

  const handleSkip = useCallback(() => {
    const clickEvent = new PlatformEvent('click', { node })
    events?.onSkip?.(clickEvent)
  }, [events, node])

  const handleScroll = useCallback((event) => {
    const { id, value } = event.detail
    scrollPositionsRef.current[id] = value
  }, [])

  useEffect(() => {
    if (!node.back) {
      scrollPositionsRef.current[node.id] = 0
    }
  }, [node])

  const labelEvent = new PlatformEvent('label', { node })
  const nodeTitle = onNodeLabel?.(labelEvent) || node.name

  const nodeAttachment = attachment && onAttachment
    ? onAttachment?.(new PlatformEvent('open', { value: attachment }))
    : attachment

  const nodeType = onNodeType(node?.type)

  const displayTitle = (nodeType === 'Directory' && node?.$children?.length)
      || nodeType === 'File'
      || nodeType === 'Url'

  return (
    <>
      { // Optionally render breadcrumbs
        !options?.showBreadcrumbs ? null : (
          <Grid
            container
            className={classes.breadcrumbs}
          >
            {Children.map(children, child => (
              child.props.hidden || child.key !== 'breadcrumbs' ? null : cloneElement(child, {
                node,
                events: {
                  onOpen: onBreadcrumbs,
                  onClick: handleClick,
                },
              })
            ))}
          </Grid>
        )}
      { // Rendering the title
        // Only render if we don't render the root node, or, if it is the root node,
        // ensure that it has children, so no empty document
        displayTitle && (
          <Title
            node={node}
            type={nodeType}
            title={nodeTitle}
            classes={classes}
            attachment={nodeAttachment}
            events={{
              onClick: handleClick,
              onFavoriteOpen: events?.onFavoriteOpen,
              onFavoriteClick: events?.onFavoriteClick,
              onLinkClick: events?.onLinkClick,
            }}
            showFavorite={options?.showFavorite}
            showDeepLink={options?.showDeepLink}
            downloadAll={options?.downloadAll}
            labels={{
              copyLink: options?.linkLabel,
              linkCopied: options?.linkCopiedLabel,
              downloadAll: options?.downloadAllLabel,
            }}
            icons={{
              favorite: options?.favoriteIcon,
              noFavorite: options?.noFavoriteIcon,
              linkIcon: options?.linkIcon,
              download: options?.downloadIcon,
            }}
          />
        )}
      { // Rendering a directory
        nodeType === 'Directory' && node?.$children?.length
          ? <Directory
            node={node}
            classes={classes}
            ListItem={ListItem}
            types={options.types}
            group={options.group}
            fileIcons={options.fileIcons}
            hideSkipIcon={options.hideSkipIcon}
            labels={{ skipLabel: options.skipLabel }}
            scrollPosition={scrollPositionsRef.current[node.id]}
            events={{
              onNodeType,
              onNodeLabel,
              onSkip: handleSkip,
              onClick: events?.onClick,
              onScroll: handleScroll,
            }}
            icons={{
              skipIcon: options.skipIcon,
              forwardIcon: options.forwardIcon,
              downloadIcon: options.downloadIcon,
              skipSingleNode: options.skipSingleNode,
              notifyOnSkippable: options.notifyOnSkippable,
            }}
          />
          : null
      }
      { // The directory has no content, display NoData component
        nodeType === 'Directory' && !node?.$children?.length
          ? Children.map(children, child => (
            child.props.hidden || child.key !== 'nodata' ? null : cloneElement(child, {
              ...child.props,
              ...options.noDataLabel && { label: options.noDataLabel },
              ...options.noDataDescription && { description: options.noDataDescription },
            })
          ))
          : null
      }
      { // Rendering a file
        nodeType === 'File' && Children.map(children, child => (
          child.props.hidden || child.key !== 'file' ? null : cloneElement(child, {
            attachment: { ...nodeAttachment, uuid: `${node.id}-preview` },
            group: options?.group,
            showToolbar: options?.showToolbar,
            showFavorite: options?.showFavorite,
            showFeedback: options?.showFeedback,
            maxPreviewSize: options?.maxPreviewSize,
            previewableTypes: options?.previewableTypes,
          })
        ))}
    </>
  )
}

/**
 * Component to display the contents of a single node.
 *
 * Can be used on its own if `events.onClick` and `events.onOpen`
 * event handlers are present. If the latter is not, it expects `node`
 * from a parent component (e.g. {@link FileExplorer}).
 *
 * @param {Object} node               the current node to render.
 * @param {Object} events             events passed from parent component
 * @param {Function} events.onClick   handler for clicking on an item.
 * @param {Function} [events.onOpen]  optional onOpen handler.
 * @param {String} className          class to apply
 * @param {String} template           template to use for rendering children
 * @param {Object} children           children to render
 * @param {React.ReactNode} feedback  component for showing the feedback form
 * @param {Object} props              additional props from parent component
 * @param {React.Ref} ref             forwarded ref
 * @returns {JSX.Element}
 */
const FileFolder = ({
  node,
  events,
  className,
  template,
  children,
  ...props
}, ref) => {
  const {
    spacing = 0,
    space = 0,
    gap = 0,
    group,
    types,
    showFeedback = false,
    showToolbar = false,
    showFavorite = false,
    showDeepLink = false,
    downloadAll = false,
    ...restProps
  } = props

  const [currentNode, setCurrentNode] = useState(null)

  const ListItem = useMemo(() => preload(template), [template])
  const theme = useTheme()

  // Node to display
  const targetNode = useMemo(() => currentNode || node || {}, [currentNode, node])
  const nodeType = useMemo(() => events?.onNodeType?.(targetNode.type), [targetNode])

  const classes = useStyles(styles, {
    largeTitle: !props?.showDeepLink || nodeType !== 'File',
  })()

  const options = {
    ...restProps,
    group,
    classes,
    listItem: ListItem,
    children,
    showFeedback,
    showToolbar,
    showFavorite,
    showDeepLink,
    downloadAll,
    types,
    theme,
  }

  // Set `currentNode` if onOpen event handler is present.
  useEffect(() => {
    (async () => {
      const newNode = await events?.onOpen?.(null)
      newNode && setCurrentNode(newNode)
    })()
  }, [])

  return (
    <ErrorBoundary>
      <Suspense fallback={null}>
        <Grid
          item
          ref={ref}
          className={`${classes.root} ${className}`}
          xs={props.xs}
          sm={props.sm}
          md={props.md}
          lg={props.lg}
          xl={props.xl}
          style={{
            padding: spacing
              ? theme.spacing(spacing)
              : theme.spacing(space, gap),
          }}
        >
          {_content(targetNode, events, options)}
        </Grid>
      </Suspense>
    </ErrorBoundary>
  )
}

export default FileFolder
